import React, { useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import type { SectionConfig } from './Navigation.types';

interface BottomNavProps {
  sections: SectionConfig[];
  currentSection: number;
  onSectionChange: (index: number) => void;
}

/**
 * Floating pill bottom navigation bar.
 *
 * - 5 icons in a rounded pill with backdrop blur
 * - Active icon highlighted with accent color + subtle scale
 * - 48 px minimum touch targets
 * - All colours from CSS variables (no hardcoded values)
 * - Safe-area padding for notched devices
 * - Uses RAF to prevent blocking UI thread on section change
 */
export function BottomNav({ sections, currentSection, onSectionChange }: BottomNavProps) {
  const pendingRef = useRef(false);

  const handleNavTap = useCallback((index: number) => {
    if (pendingRef.current) return; // debounce rapid taps
    if (index === currentSection) return; // already on this section
    pendingRef.current = true;
    requestAnimationFrame(() => {
      onSectionChange(index);
      // Allow next tap after a short cooldown
      setTimeout(() => { pendingRef.current = false; }, 200);
    });
  }, [currentSection, onSectionChange]);

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 flex justify-center"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 8px)' }}
    >
      <div
        className="flex items-center gap-1 px-3 py-2 mx-4 mb-2 rounded-full"
        style={{
          background: 'var(--tab-bar-bg, rgba(0, 0, 0, 0.7))',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.3)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
        }}
      >
        {sections.map((section, index) => {
          const isActive = index === currentSection;
          const Icon = section.icon;

          return (
            <button
              key={section.id}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleNavTap(index);
              }}
              aria-label={section.label}
              aria-current={isActive ? 'page' : undefined}
              className="relative flex flex-col items-center justify-center outline-none focus-visible:ring-2 focus-visible:ring-offset-2 rounded-full touch-none"
              style={{
                minWidth: 48,
                minHeight: 48,
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              {/* Active background pill */}
              {isActive && (
                <motion.div
                  layoutId="nav-active-bg"
                  className="absolute inset-1 rounded-full"
                  style={{ background: 'var(--color-accent, #06b6d4)', opacity: 0.15 }}
                  transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                />
              )}

              <motion.div
                animate={{
                  scale: isActive ? 1.15 : 1,
                  y: isActive ? -2 : 0,
                }}
                transition={{ type: 'spring', damping: 20, stiffness: 300 }}
              >
                <Icon
                  size={22}
                  strokeWidth={isActive ? 2.4 : 1.8}
                  style={{
                    color: isActive
                      ? 'var(--color-accent, #06b6d4)'
                      : 'var(--text-color, #ffffff)',
                    opacity: isActive ? 1 : 0.5,
                    transition: 'color 0.2s, opacity 0.2s',
                  }}
                />
              </motion.div>

              {/* Label only on active tab */}
              {isActive && (
                <motion.span
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 4 }}
                  className="text-[10px] font-semibold mt-0.5 leading-none"
                  style={{ color: 'var(--color-accent, #06b6d4)' }}
                >
                  {section.label}
                </motion.span>
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
